import { useState, useRef, useCallback, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Staff AUDIO conference (mesh) — smallest real multi-party version.
//
// Architecture: full-mesh WebRTC (one RTCPeerConnection per remote participant),
// star-signaled — the host distributes the participant roster; every peer then
// connects to every other peer. Signaling rides the SAME WebSocket as 1:1 calls
// via conf-* message types (forwarded by the backend's target-based send_to_user;
// no backend change). Each peer connection reuses the v113 reliability pattern:
// ICE candidates are buffered until setRemoteDescription, then flushed.
//
// Glare avoidance: for any pair, the peer with the lexicographically smaller
// connection id is the offerer; the other waits for the offer.
//
// Resilience (2026-07 hardening — web-only, no backend change):
//   - conf-ice arriving before the peer exists no longer poisons the offer path
//     (the {pc:null} stub is adopted by makePeer instead of blocking connectToPeer).
//   - A periodic self-heal heartbeat reaps failed peers and re-forms the mesh;
//     the host also re-broadcasts the roster so a dropped roster message recovers.
//   - On WebSocket reconnect (tab background/minimize/suspend), the conference
//     resyncs: participants re-announce, failed peers renegotiate. selfId is
//     stable across reconnects, so peer identities survive.
//   - Media readiness is tracked separately from RTCPeerConnection state, so a
//     participant is only "Connected" once audio actually flows; autoplay blocks
//     surface a "Tap to enable audio" affordance (handled in ConferenceUI).
//   - Host can REMOVE a participant (conf-remove) and RE-ADD them (invite state
//     is pruned on decline/leave/remove/timeout).
//
// Does NOT touch the proven 1:1 call path — it only consumes the additive
// plumbing (wsSend / setExternalConfHandler / selfId / iceServers / isConnected).
// ---------------------------------------------------------------------------

const cdbg = (...a) => { try { if (window.localStorage.getItem('webrtc_debug') === '1') console.log('[CONF]', ...a); } catch (e) {} };
const baseOf = (c) => (c || '').split('__')[0];

const HEARTBEAT_MS = 5000;   // self-heal cadence while a conference is active
const INVITE_TIMEOUT_MS = 35000;

export function useConference(rtc) {
  const { wsSend, setExternalConfHandler, selfId, iceServers, resolveStaff, inCall, isConnected } = rtc || {};

  const [conference, setConference] = useState(null);   // {id, isHost} | null
  const [participants, setParticipants] = useState([]); // [{connId, name, role, state, media, stream}]
  const [incomingInvite, setIncomingInvite] = useState(null); // {id, hostConn, hostName}
  const [selfMuted, setSelfMuted] = useState(false);
  const [error, setError] = useState('');

  const peersRef = useRef(new Map());      // connId -> { pc, pending:[], remoteSet:bool }
  const localStreamRef = useRef(null);
  const confIdRef = useRef(null);
  const isHostRef = useRef(false);
  const hostConnRef = useRef(null);
  const rosterRef = useRef([]);            // all participant connIds incl self (host-authoritative)
  const invitedRef = useRef(new Set());    // base uids invited (host)
  const inviteTimersRef = useRef(new Map()); // base uid -> setTimeout id (no-answer reaper)
  const prevConnRef = useRef(false);       // last isConnected (WS reconnect edge detect)

  const nameFor = useCallback((connId) => {
    const r = resolveStaff && resolveStaff(connId);
    // D3: when the staff roster can't resolve a participant, show a friendly label — never a uid fragment.
    return r ? r : { name: 'Staff member', role: '' };
  }, [resolveStaff]);

  // ---- participant table helpers ----
  const upsertParticipant = useCallback((connId, patch) => {
    setParticipants((prev) => {
      const i = prev.findIndex((p) => p.connId === connId);
      if (i === -1) {
        const meta = nameFor(connId);
        return [...prev, { connId, name: meta.name, role: meta.role, state: 'connecting', media: null, stream: null, ...patch }];
      }
      const next = prev.slice();
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }, [nameFor]);

  const removeParticipant = useCallback((connId) => {
    setParticipants((prev) => prev.filter((p) => p.connId !== connId));
  }, []);

  const reportMedia = useCallback((connId, media) => {
    upsertParticipant(connId, { media });
  }, [upsertParticipant]);

  const clearInviteTimer = useCallback((base) => {
    const t = inviteTimersRef.current.get(base);
    if (t) { clearTimeout(t); inviteTimersRef.current.delete(base); }
  }, []);

  // ---- local audio (audio-only conference) ----
  const ensureLocalAudio = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localStreamRef.current = stream;
    // keep mute state consistent if user muted before their audio existed
    stream.getAudioTracks().forEach((tr) => { tr.enabled = !selfMuted; });
    return stream;
  }, [selfMuted]);

  // ---- one peer connection per remote participant ----
  const makePeer = useCallback((peerConnId) => {
    let entry = peersRef.current.get(peerConnId);
    if (entry && entry.pc) return entry;
    // Adopt any ICE candidates buffered under a {pc:null} stub (candidate arrived before the peer).
    const pending = (entry && entry.pending) ? entry.pending : [];
    const pc = new RTCPeerConnection({ iceServers: iceServers && iceServers.length ? iceServers : [{ urls: ['stun:stun.l.google.com:19302'] }] });
    entry = { pc, pending, remoteSet: false };
    peersRef.current.set(peerConnId, entry);

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    }
    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        wsSend({ type: 'conf-ice', conference_id: confIdRef.current, target: peerConnId, candidate: ev.candidate });
      }
    };
    pc.ontrack = (ev) => {
      cdbg('ontrack from', peerConnId);
      // stream arrived; media becomes 'ready' only once the <audio> actually plays (reportMedia).
      upsertParticipant(peerConnId, { stream: ev.streams[0], media: 'pending' });
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      cdbg('peer', peerConnId, 'conn=', st);
      // 'disconnected' is often transient (ICE may recover) — surface as reconnecting, don't reap here.
      upsertParticipant(peerConnId, { state: st === 'disconnected' ? 'reconnecting' : st });
      // failed/closed peers are reaped + rebuilt by the heartbeat (reapAndReconnect).
    };
    upsertParticipant(peerConnId, { state: 'connecting' });
    return entry;
  }, [iceServers, wsSend, upsertParticipant]);

  const flushPeer = useCallback(async (peerConnId) => {
    const entry = peersRef.current.get(peerConnId);
    if (!entry || !entry.pc || !entry.pc.remoteDescription) return;
    const q = entry.pending; entry.pending = [];
    for (const c of q) { try { await entry.pc.addIceCandidate(c); } catch (e) { cdbg('flush addIce FAIL', e?.name); } }
  }, []);

  // offerer rule: smaller connId offers
  const connectToPeer = useCallback(async (peerConnId) => {
    if (peerConnId === selfId) return;
    const existing = peersRef.current.get(peerConnId);
    if (existing && existing.pc) return; // already has a real peer connection (a {pc:null} ICE stub does NOT block)
    if (selfId < peerConnId) {
      // we are the offerer
      try {
        await ensureLocalAudio();
        const { pc } = makePeer(peerConnId);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        wsSend({ type: 'conf-offer', conference_id: confIdRef.current, target: peerConnId, sdp: offer });
        cdbg('sent offer to', peerConnId);
      } catch (e) { cdbg('connectToPeer offer err', e?.message); }
    }
    // else: wait for their conf-offer
  }, [selfId, ensureLocalAudio, makePeer, wsSend]);

  const meshConnect = useCallback(async (roster) => {
    rosterRef.current = roster || [];
    for (const cid of rosterRef.current) {
      if (cid !== selfId) await connectToPeer(cid);
    }
  }, [selfId, connectToPeer]);

  // ---- self-heal: reap failed peers, re-form the mesh, host re-broadcasts roster ----
  const reapAndReconnect = useCallback(async () => {
    if (!confIdRef.current) return;
    peersRef.current.forEach((e, cid) => {
      const st = e.pc && e.pc.connectionState;
      if (!e.pc || st === 'failed' || st === 'closed') {
        try { e.pc && e.pc.close(); } catch (x) {}
        peersRef.current.delete(cid);
        upsertParticipant(cid, { state: 'reconnecting' });
      }
    });
    if (isHostRef.current) {
      rosterRef.current.forEach((cid) => {
        if (cid !== selfId) wsSend({ type: 'conf-roster', conference_id: confIdRef.current, target: cid, participants: rosterRef.current });
      });
    }
    await meshConnect(rosterRef.current);
  }, [selfId, wsSend, meshConnect, upsertParticipant]);

  // ---- teardown ----
  const teardown = useCallback(() => {
    peersRef.current.forEach((e) => { try { e.pc && e.pc.close(); } catch (x) {} });
    peersRef.current.clear();
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach((t) => t.stop()); localStreamRef.current = null; }
    inviteTimersRef.current.forEach((t) => clearTimeout(t)); inviteTimersRef.current.clear();
    confIdRef.current = null; isHostRef.current = false; hostConnRef.current = null;
    rosterRef.current = []; invitedRef.current = new Set();
    setParticipants([]); setConference(null); setSelfMuted(false);
  }, []);

  // ---- signaling handler (conf-*) ----
  const onConfMessage = useCallback(async (msg) => {
    const t = msg.type;
    const from = msg.sender;              // backend stamps the sender conn id
    const cid = msg.conference_id;
    try {
      if (t === 'conf-invite') {
        // ignore if already in a conference or a 1:1 call
        if (confIdRef.current || inCall) { wsSend({ type: 'conf-reject', conference_id: cid, target: from }); return; }
        setIncomingInvite({ id: cid, hostConn: from, hostName: (nameFor(from).name || 'A teammate') });
        return;
      }
      if (t === 'conf-remove') {
        // The host removed us from this conference.
        if (confIdRef.current && cid === confIdRef.current) { teardown(); }
        return;
      }
      // all remaining messages must match the active conference
      if (!confIdRef.current || cid !== confIdRef.current) {
        // host may still receive accept/reject for the conf it started
        if (!(isHostRef.current && (t === 'conf-accept' || t === 'conf-reject'))) return;
      }
      if (t === 'conf-accept') {
        if (!isHostRef.current) return;
        clearInviteTimer(baseOf(from));
        removeParticipant(baseOf(from));   // drop the optimistic 'invited' row keyed by base uid
        if (!rosterRef.current.includes(from)) rosterRef.current = [...rosterRef.current, from];
        upsertParticipant(from, { state: 'connecting' });
        // tell EVERYONE (incl the newcomer) the full roster so the mesh forms
        rosterRef.current.forEach((cidp) => {
          if (cidp !== selfId) wsSend({ type: 'conf-roster', conference_id: confIdRef.current, target: cidp, participants: rosterRef.current });
        });
        await meshConnect(rosterRef.current);
        return;
      }
      if (t === 'conf-reject') {
        if (isHostRef.current) {
          clearInviteTimer(baseOf(from));
          invitedRef.current.delete(baseOf(from));          // allow re-invite
          upsertParticipant(baseOf(from), { state: 'declined' });
          setTimeout(() => removeParticipant(baseOf(from)), 4000);
        }
        return;
      }
      if (t === 'conf-roster') {
        if (isHostRef.current) return;   // host is roster-authoritative
        const roster = msg.participants || [];
        rosterRef.current = roster;
        // prune peers no longer in the roster (propagates a host remove)
        peersRef.current.forEach((e, pid) => {
          if (pid !== selfId && !roster.includes(pid)) {
            try { e.pc && e.pc.close(); } catch (x) {}
            peersRef.current.delete(pid);
            removeParticipant(pid);
          }
        });
        await meshConnect(roster);
        return;
      }
      if (t === 'conf-offer') {
        await ensureLocalAudio();
        const entry = makePeer(from);
        await entry.pc.setRemoteDescription(msg.sdp);
        entry.remoteSet = true;
        await flushPeer(from);
        const answer = await entry.pc.createAnswer();
        await entry.pc.setLocalDescription(answer);
        wsSend({ type: 'conf-answer', conference_id: confIdRef.current, target: from, sdp: answer });
        cdbg('answered', from);
        return;
      }
      if (t === 'conf-answer') {
        const entry = peersRef.current.get(from);
        if (entry && entry.pc) { await entry.pc.setRemoteDescription(msg.sdp); entry.remoteSet = true; await flushPeer(from); }
        return;
      }
      if (t === 'conf-ice') {
        const entry = peersRef.current.get(from);
        if (entry && entry.pc && entry.remoteSet && entry.pc.remoteDescription) {
          try { await entry.pc.addIceCandidate(msg.candidate); } catch (e) { cdbg('addIce FAIL', e?.name); }
        } else {
          // buffer until the peer exists / remote description is set (adopted by makePeer)
          if (!entry) { const e2 = { pc: null, pending: [msg.candidate], remoteSet: false }; peersRef.current.set(from, e2); }
          else entry.pending.push(msg.candidate);
        }
        return;
      }
      if (t === 'conf-leave') {
        const entry = peersRef.current.get(from);
        if (entry) { try { entry.pc && entry.pc.close(); } catch (x) {} peersRef.current.delete(from); }
        rosterRef.current = rosterRef.current.filter((c) => c !== from);
        removeParticipant(from);
        invitedRef.current.delete(baseOf(from));            // allow re-invite
        if (isHostRef.current) {
          rosterRef.current.forEach((cid2) => { if (cid2 !== selfId) wsSend({ type: 'conf-roster', conference_id: confIdRef.current, target: cid2, participants: rosterRef.current }); });
        }
        return;
      }
      if (t === 'conf-end') {
        teardown();
        return;
      }
    } catch (e) { cdbg('onConfMessage err', t, e?.message); }
  }, [inCall, wsSend, nameFor, upsertParticipant, removeParticipant, meshConnect, ensureLocalAudio, makePeer, flushPeer, selfId, teardown, clearInviteTimer]);

  // register the handler with useWebRTC (once)
  useEffect(() => {
    if (setExternalConfHandler) setExternalConfHandler(onConfMessage);
    return () => { if (setExternalConfHandler) setExternalConfHandler(null); };
  }, [setExternalConfHandler, onConfMessage]);

  // Self-heal heartbeat while a conference is active (reaps failed peers, re-forms mesh).
  useEffect(() => {
    if (!conference) return undefined;
    const iv = setInterval(() => { reapAndReconnect(); }, HEARTBEAT_MS);
    return () => clearInterval(iv);
  }, [conference, reapAndReconnect]);

  // WebSocket reconnect edge (tab background/minimize/suspend recovery): resync the conference.
  useEffect(() => {
    const was = prevConnRef.current;
    prevConnRef.current = !!isConnected;
    if (!was && isConnected && confIdRef.current) {
      // participant re-announces to the host so it re-adds us + re-broadcasts the roster
      if (!isHostRef.current && hostConnRef.current) {
        wsSend({ type: 'conf-accept', conference_id: confIdRef.current, target: hostConnRef.current });
      }
      reapAndReconnect();
    }
  }, [isConnected, wsSend, reapAndReconnect]);

  // ---- public API ----
  const inviteMember = useCallback((baseUid) => {
    if (!confIdRef.current || !isHostRef.current || !baseUid) return false;
    if (invitedRef.current.has(baseUid)) return false;
    invitedRef.current.add(baseUid);
    wsSend({ type: 'conf-invite', conference_id: confIdRef.current, target: baseUid });
    // optimistic "inviting" row keyed by base uid (reconciled to the real conn on accept)
    upsertParticipant(baseUid, { state: 'invited' });
    // no-answer reaper: if they never accept, clear the invite so the host can re-invite
    clearInviteTimer(baseUid);
    const tid = setTimeout(() => {
      inviteTimersRef.current.delete(baseUid);
      setParticipants((prev) => {
        const p = prev.find((x) => x.connId === baseUid);
        if (p && p.state === 'invited') {
          invitedRef.current.delete(baseUid);
          setTimeout(() => removeParticipant(baseUid), 4000);
          return prev.map((x) => (x.connId === baseUid ? { ...x, state: 'timedout' } : x));
        }
        return prev;
      });
    }, INVITE_TIMEOUT_MS);
    inviteTimersRef.current.set(baseUid, tid);
    return true;
  }, [wsSend, upsertParticipant, removeParticipant, clearInviteTimer]);

  const removeMember = useCallback((connId) => {
    if (!isHostRef.current || !confIdRef.current || !connId) return false;
    const base = baseOf(connId);
    wsSend({ type: 'conf-remove', conference_id: confIdRef.current, target: connId });
    const e = peersRef.current.get(connId);
    if (e) { try { e.pc && e.pc.close(); } catch (x) {} peersRef.current.delete(connId); }
    rosterRef.current = rosterRef.current.filter((c) => c !== connId);
    // tell the remaining participants to drop this peer too
    rosterRef.current.forEach((cid) => {
      if (cid !== selfId) wsSend({ type: 'conf-roster', conference_id: confIdRef.current, target: cid, participants: rosterRef.current });
    });
    removeParticipant(connId);
    removeParticipant(base);                 // also drop any lingering optimistic row
    invitedRef.current.delete(base);         // allow re-add
    clearInviteTimer(base);
    return true;
  }, [wsSend, selfId, removeParticipant, clearInviteTimer]);

  const startConference = useCallback(async (inviteeBaseUids = []) => {
    setError('');
    if (inCall) { setError('Finish your current call before starting a conference.'); return false; }
    if (confIdRef.current) return false;
    if (!selfId) { setError('Not connected to signaling.'); return false; }
    try {
      await ensureLocalAudio();
    } catch (e) { setError('Microphone permission is required for a conference.'); return false; }
    const id = `conf_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    confIdRef.current = id; isHostRef.current = true; hostConnRef.current = selfId;
    rosterRef.current = [selfId];
    invitedRef.current = new Set();
    setConference({ id, isHost: true });
    setParticipants([]); // self is implicit (shown separately in UI)
    (inviteeBaseUids || []).forEach((b) => inviteMember(b));
    return true;
  }, [inCall, selfId, ensureLocalAudio, inviteMember]);

  const acceptInvite = useCallback(async () => {
    const inv = incomingInvite;
    if (!inv) return false;
    if (inCall) { setError('Finish your current call first.'); return false; }
    try { await ensureLocalAudio(); }
    catch (e) { setError('Microphone permission is required.'); wsSend({ type: 'conf-reject', conference_id: inv.id, target: inv.hostConn }); setIncomingInvite(null); return false; }
    confIdRef.current = inv.id; isHostRef.current = false; hostConnRef.current = inv.hostConn;
    rosterRef.current = [selfId];
    setConference({ id: inv.id, isHost: false });
    setParticipants([]);
    setIncomingInvite(null);
    wsSend({ type: 'conf-accept', conference_id: inv.id, target: inv.hostConn });
    return true;
  }, [incomingInvite, inCall, ensureLocalAudio, wsSend, selfId]);

  const rejectInvite = useCallback(() => {
    const inv = incomingInvite;
    if (inv) wsSend({ type: 'conf-reject', conference_id: inv.id, target: inv.hostConn });
    setIncomingInvite(null);
  }, [incomingInvite, wsSend]);

  const toggleMute = useCallback(() => {
    const s = localStreamRef.current; if (!s) return false;
    const next = !selfMuted;
    s.getAudioTracks().forEach((tr) => { tr.enabled = !next; });
    setSelfMuted(next);
    return next;
  }, [selfMuted]);

  const leaveConference = useCallback(() => {
    if (!confIdRef.current) return;
    const id = confIdRef.current;
    if (isHostRef.current) {
      // host ends for everyone
      rosterRef.current.forEach((cid) => { if (cid !== selfId) wsSend({ type: 'conf-end', conference_id: id, target: cid }); });
    } else {
      // notify peers we are leaving
      rosterRef.current.forEach((cid) => { if (cid !== selfId) wsSend({ type: 'conf-leave', conference_id: id, target: cid }); });
    }
    teardown();
  }, [wsSend, selfId, teardown]);

  return {
    conference,
    participants,
    incomingInvite,
    selfMuted,
    confError: error,
    isConfHost: !!(conference && conference.isHost),
    startConference,
    inviteMember,
    removeMember,
    reportMedia,
    acceptInvite,
    rejectInvite,
    toggleConfMute: toggleMute,
    leaveConference,
  };
}
